import React, { Suspense } from "react";

import { injectGlobal } from "@emotion/css";
import { QueryAwait } from "@steto-care/steto-query";
import { createRoot } from "react-dom/client";

import "react-toastify/dist/ReactToastify.css";

import { AppReady } from "@/AppReady.tsx";
import { AuthProvider } from "@/auth/auth.ts";
import { InitAuth } from "@/auth/InitAuth.tsx";
import { LoginBouncer } from "@/auth/LoginBouncer.tsx";
import { I18nWrapper } from "@/intl/i18n.tsx";
import { initSentry } from "@/observation_of_users/Sentry.tsx";
import { TrackingWrapper } from "@/observation_of_users/TrackingWrapper.tsx";
import { QueriesWrapper } from "@/queries/QueriesWrapper.tsx";
import { MuiWrapper } from "@/theme/MuiWrapper.tsx";
import { CustomToastContainer } from "@/uiKit/atoms/toast/Toast";
import { CriticalErrorPage } from "@/uiKit/pages/CriticalErrorPage.tsx";
import { LoadingPage } from "@/uiKit/pages/LoadingPage.tsx";
import "@/utils/stringUtils";

const container = document.getElementById("root");

// Before render: InitAuth fires the first api call from an effect, and anything
// it reports would be dropped if Sentry were still initializing in a wrapper.
initSentry();

/**
 * Component order:
 * - MuiWrapper: Material-UI theme initialization, must be before Suspense since LoadingPage relies on MUI
 * - Suspense : Handles loading states for lazy-loaded components
 * - TODO add error boundary ?
 * - I18nextProvider : Provides internationalization context
 * - QueriesWrapper : Tanstack Query client initialization, must be above the auth
 *        layer since restore / login / logout are mutations
 * - AuthProvider : Holds the session (starts 'pending' until the refresh token is checked)
 * - TrackingWrapper : Initializes user tracking with PostHog
 * - StrictMode: Ensures components follow React's best practices,
 *        below TrackingWrapper so PostHog isn't initialized twice
 * - CustomToastContainer: Global toast notifications, above the bouncer so Login can toast
 * - InitAuth : Restores the session from the stored refresh token
 * - LoginBouncer : LoadingPage while pending, Login while unauthenticated, the app once signed in
 * - QueryAwait : Suspense + error boundary for the authenticated tree only — above the
 *        bouncer it would replace the login page itself with a fallback
 * - AppReady : App is ready to run
 */
createRoot(container as HTMLElement).render(
  <MuiWrapper>
    <Suspense fallback={<LoadingPage />}>
      <I18nWrapper>
        <QueriesWrapper>
          <AuthProvider status="pending">
            <TrackingWrapper>
              <React.StrictMode>
                <CustomToastContainer />
                <InitAuth />
                <LoginBouncer>
                  <QueryAwait errorFallback={CriticalErrorPage} pendingFallback={LoadingPage}>
                    <AppReady />
                  </QueryAwait>
                </LoginBouncer>
              </React.StrictMode>
            </TrackingWrapper>
          </AuthProvider>
        </QueriesWrapper>
      </I18nWrapper>
    </Suspense>
  </MuiWrapper>,
);

injectGlobal`
    input, a, fieldset, p, ul { all: unset; }
    
    button { all: unset; cursor: pointer; }
    
    html, body, .root {
        width: 100%;
        height: 100%;
    }
    
    .scrollbar {
      scrollbar-color: lightgrey transparent; 
      ::-webkit-scrollbar {
        background-color: transparent;
      },
    }

    .no-scrollbar {
      scrollbar-width: none;
      scrollbar-color: transparent transparent;
      scrollbar-gutter: stable;
      ::-webkit-scrollbar {
        display: none;
        background-color: transparent;
      }
    }
`;
